import { useCallback, useEffect, useState } from 'react';

import { api, type ArchivumValasz, type EvAllapot, type Idoszak, type NapStatusz } from '../api.ts';
import { ft, szam } from '../formazas.ts';

/**
 * Évzárás. (A megrendelő kérése, 2026-09-17)
 *
 * Évenként: hány rögzített nap van, mennyi lezárt, és ha minden kész, egy
 * gombbal lezárható. A lezárt év adata ugyanúgy kereshető marad; a zárás egy
 * végleges mentést készít, és a napok onnantól csak megerősítéssel nyithatók
 * vissza.
 */
function Evzaras() {
  const [evek, setEvek] = useState<EvAllapot[] | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  const [uzenet, setUzenet] = useState<string | null>(null);
  const [munkaban, setMunkaban] = useState(false);

  const betolt = useCallback(async () => {
    try {
      setEvek((await api.get<{ evek: EvAllapot[] }>('/api/keszlet/evzaras')).evek);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Nem sikerült betölteni.');
    }
  }, []);
  useEffect(() => {
    void betolt();
  }, [betolt]);

  async function lezar(ev: number) {
    const igen = window.confirm(
      `Lezárod a ${ev}. évet?\n\n` +
        'A program végleges mentést készít az évről, és az év napjait onnantól csak ' +
        'külön megerősítéssel lehet visszanyitni. Az adat ugyanúgy kereshető marad.',
    );
    if (!igen) return;
    setMunkaban(true);
    setHiba(null);
    try {
      const v = await api.post<{ ev: EvAllapot; evek: EvAllapot[] }>(`/api/keszlet/evzaras/${ev}`);
      setEvek(v.evek);
      setUzenet(`A ${ev}. év lezárva. Az évzárási mentés: ${v.ev.lezarva?.mentes ?? ''}`);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Az évzárás nem sikerült.');
    } finally {
      setMunkaban(false);
    }
  }

  return (
    <div className="tdet">
      <h4>Évzárás</h4>
      {hiba && <div className="uzenet bad">{hiba}</div>}
      {uzenet && <div className="uzenet ok">{uzenet}</div>}
      <table className="tt2" style={{ marginBottom: 8 }}>
        <thead>
          <tr>
            <th>Év</th>
            <th className="n">Rögzített nap</th>
            <th className="n">Lezárt</th>
            <th>Állapot</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {(evek ?? []).map((e) => (
            <tr key={e.ev}>
              <td style={{ padding: '6px 10px', fontWeight: 600 }}>{e.ev}</td>
              <td className="calc2">{e.napok}</td>
              <td className="calc2">{e.lezart_napok}</td>
              <td style={{ padding: '6px 10px', fontSize: 11.5 }}>
                {e.lezarva ? (
                  <span style={{ color: 'var(--ok)' }}>
                    lezárva {e.lezarva.ido.slice(0, 10)} · mentés: {e.lezarva.mentes}
                  </span>
                ) : (
                  <span className={e.nyitott_napok.length ? 'bad2' : 'halvany'} title={e.nyitott_napok.join(', ')}>
                    {e.akadaly ?? 'lezárható'}
                  </span>
                )}
              </td>
              <td className="act" style={{ width: 120 }}>
                {!e.lezarva && (
                  <button
                    className="btn sm pri"
                    disabled={!e.lezarhato || munkaban}
                    title={e.akadaly ?? undefined}
                    onClick={() => void lezar(e.ev)}
                  >
                    Év lezárása
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <span className="hint">
        A lezárt év napjai ugyanúgy kereshetők (Adott év szűrő), a kimutatásokban is benne
        vannak. A zárás után csak megerősítéssel nyithatók vissza, és minden módosítás naplózódik.
      </span>
    </div>
  );
}

/** Az időszaksáv gombjai, a megbízás sorrendjében. (2.0 / 5.11) */
const IDOSZAKOK: Idoszak[] = ['30nap', '3honap', 'honap', 'ev', 'mind'];

const STATUSZOK: Array<NapStatusz | ''> = ['', 'tervezes', 'kiadva', 'ellenorzes', 'lezarva'];

/**
 * Járatlapok archívuma. (Megbízás 6.7)
 *
 * Minden nap egy sorban: állapot, címszám, tervezett és ellenőrzött liter,
 * kitöltöttség, lezárt sofőrök, leadó. Innen lehet bármelyik naphoz átugrani.
 *
 * Két figyelmeztetés is itt jelenik meg — ezek arra valók, hogy egy nap ne
 * maradjon örökre félkészen:
 *  - ha egy korábbi nap még nyitva van, miközben későbbit már lezártál,
 *  - ha kettőnél több nap vár ellenőrzésre.
 */
export function Jaratlapok({
  onSzerkesztes,
  onEllenorzes,
  onNyomtatas,
}: {
  onSzerkesztes: (datum: string) => void;
  onEllenorzes: (datum: string) => void;
  onNyomtatas: (datum: string) => void;
}) {
  const [archivum, setArchivum] = useState<ArchivumValasz | null>(null);
  const [szuro, setSzuro] = useState<NapStatusz | ''>('');
  /**
   * Időszak. (2.0 / 5.11) Alapérték három hónap: ennyi elég a napi
   * munkához, a teljes lista viszont évek múlva már lassú lenne.
   */
  const [idoszak, setIdoszak] = useState<Idoszak>('3honap');
  const [honap, setHonap] = useState('');
  const [ev, setEv] = useState('');
  const [evzarasNyitva, setEvzarasNyitva] = useState(false);
  /** Szabad dátumpár — ha ki van töltve, felülírja a gombokat. */
  const [mettol, setMettol] = useState('');
  const [meddig, setMeddig] = useState('');
  const [hiba, setHiba] = useState<string | null>(null);
  const [toltes, setToltes] = useState(true);

  const betolt = useCallback(async () => {
    setToltes(true);
    try {
      const p = new URLSearchParams();
      if (szuro) p.set('statusz', szuro);
      p.set('idoszak', idoszak);
      if (honap) p.set('honap', honap);
      if (ev) p.set('ev', ev);
      if (mettol) p.set('mettol', mettol);
      if (meddig) p.set('meddig', meddig);
      setArchivum(await api.get<ArchivumValasz>(`/api/keszlet/archivum?${p}`));
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Nem sikerült betölteni az archívumot.');
    } finally {
      setToltes(false);
    }
  }, [szuro, idoszak, honap, ev, mettol, meddig]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  const sorok = archivum?.sorok ?? [];

  return (
    <>
      <div className="dayhead">
        <span style={{ fontSize: 15, fontWeight: 600 }}>Járatlapok</span>
        <span className="hint">Minden nap egy sorban, a legfrissebb elöl.</span>
        <button className="btn sm" onClick={() => setEvzarasNyitva((v) => !v)}>
          {evzarasNyitva ? '× Évzárás' : 'Évzárás'}
        </button>
        <div className="stats">
          <div>
            Nap<strong>{sorok.length}</strong>
          </div>
        </div>
      </div>

      {evzarasNyitva && <Evzaras />}

      <div className="idosav">
        {IDOSZAKOK.map((i) => (
          <button
            key={i}
            className={`fchip${idoszak === i && !archivum?.szures.sajat_idoszak ? ' on' : ''}`}
            onClick={() => {
              setIdoszak(i);
              setMettol('');
              setMeddig('');
            }}
          >
            {archivum?.idoszak_nevek[i] ?? i}
            <b>{archivum?.idoszak_darabok[i] ?? 0}</b>
          </button>
        ))}

        {idoszak === 'honap' && (
          <select
            value={honap || (archivum?.szures.honap ?? '')}
            onChange={(e) => {
              setHonap(e.target.value);
              setMettol('');
              setMeddig('');
            }}
          >
            {(archivum?.honapok ?? []).map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </select>
        )}

        {idoszak === 'ev' && (
          <select
            value={ev || (archivum?.szures.ev ?? '')}
            onChange={(e) => {
              setEv(e.target.value);
              setMettol('');
              setMeddig('');
            }}
          >
            {(archivum?.evek ?? []).map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
        )}

        <span className="idoelvalaszto" />
        <label className="idomezo">
          -tól
          <input type="date" value={mettol} onChange={(e) => setMettol(e.target.value)} />
        </label>
        <label className="idomezo">
          -ig
          <input type="date" value={meddig} onChange={(e) => setMeddig(e.target.value)} />
        </label>
        {archivum?.szures.sajat_idoszak && (
          <button
            className="btn sm"
            title="Vissza a gombokhoz"
            onClick={() => {
              setMettol('');
              setMeddig('');
            }}
          >
            Dátumok törlése
          </button>
        )}
      </div>

      <div className="fchips">
        {STATUSZOK.map((s) => (
          <button
            key={s || 'mind'}
            className={`fchip${szuro === s ? ' on' : ''}`}
            onClick={() => setSzuro(s)}
          >
            {s === '' ? 'Mind' : (archivum?.statusz_nevek[s] ?? s)}
            <b>
              {s === ''
                ? Object.values(archivum?.statusz_darabok ?? {}).reduce((a, b) => a + b, 0)
                : (archivum?.statusz_darabok[s] ?? 0)}
            </b>
          </button>
        ))}
      </div>

      {hiba && <div className="uzenet bad">{hiba}</div>}

      <div className="warns">
        {(archivum?.figyelmeztetesek ?? []).map((f) => (
          <div className="warn" key={f.fajta}>
            <b>{f.szoveg}</b>
            {f.datumok.length > 0 && (
              <div style={{ marginTop: 3, fontSize: 11.5 }}>
                {f.datumok.slice(0, 10).join(' · ')}
                {f.datumok.length > 10 ? ` … és még ${f.datumok.length - 10}` : ''}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="matrix">
        <table className="tt2">
          <thead>
            <tr>
              <th>Nap</th>
              <th>Állapot</th>
              <th className="n">Cím</th>
              <th className="n">Tervezett l</th>
              <th className="n">Ellenőrzött l</th>
              <th className="n">Kitöltöttség</th>
              <th className="n">Lezárt sofőr</th>
              <th className="n">Leadó</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorok.map((s) => (
              <tr key={s.datum}>
                <td style={{ padding: '8px 10px', fontWeight: 550, whiteSpace: 'nowrap' }}>
                  {s.felirat}
                  {s.lezarva_ekkor && (
                    <small className="halvany" style={{ display: 'block' }}>
                      lezárta: {s.lezarta}
                    </small>
                  )}
                </td>
                <td style={{ padding: '8px 10px' }}>
                  <span className={`allapotjel ${s.statusz}`}>
                    {archivum?.statusz_nevek[s.statusz] ?? s.statusz}
                  </span>
                </td>
                <td className="calc2">{s.cimek}</td>
                <td className="calc2">{s.terv_liter ? szam(s.terv_liter, 0) : '—'}</td>
                <td className="calc2">{s.teny_liter ? szam(s.teny_liter, 0) : '—'}</td>
                <td className="calc2">
                  {s.cimek === 0 ? (
                    '—'
                  ) : (
                    <span
                      className={s.kitoltott < s.cimek ? 'bad2' : undefined}
                      title={`${s.kitoltott} cím van sofőrhöz rendelve a ${s.cimek}-ből`}
                    >
                      {s.kitoltott} / {s.cimek}
                    </span>
                  )}
                </td>
                <td className="calc2">
                  {s.megkezdett_soforok === 0
                    ? '—'
                    : `${s.lezart_soforok} / ${s.megkezdett_soforok}`}
                </td>
                <td className="calc2">
                  <b>{s.leado ? ft(s.leado) : '—'}</b>
                </td>
                <td className="act" style={{ width: 230, whiteSpace: 'nowrap' }}>
                  <button
                    className="btn sm"
                    disabled={s.statusz === 'lezarva'}
                    title={
                      s.statusz === 'lezarva'
                        ? 'A lezárt nap terve nem szerkeszthető.'
                        : undefined
                    }
                    onClick={() => onSzerkesztes(s.datum)}
                  >
                    Szerkesztés
                  </button>{' '}
                  <button
                    className="btn sm"
                    disabled={s.statusz === 'tervezes'}
                    title={
                      s.statusz === 'tervezes'
                        ? 'Előbb ki kell adni a járatlapot.'
                        : undefined
                    }
                    onClick={() => onEllenorzes(s.datum)}
                  >
                    Ellenőrzés
                  </button>{' '}
                  <button
                    className="btn sm"
                    disabled={s.cimek === 0}
                    onClick={() => onNyomtatas(s.datum)}
                  >
                    Nyomtatás
                  </button>
                </td>
              </tr>
            ))}
            {sorok.length === 0 && (
              <tr>
                <td colSpan={9} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
                  {toltes
                    ? 'Betöltés…'
                    : szuro
                      ? 'Ebben az állapotban nincs nap.'
                      : 'Még nincs egyetlen járatlap sem. A Járatszervezésen állítsd össze az elsőt.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
